/**
 * The event header under the top bar — host event edit handoff §"Event header".
 *
 *   ← MY EVENTS · `HOST DASHBOARD` (or `HOST · NEW EVENT`) + the status pill · the title ·
 *   the when line · and, in edit mode, the stat pills (SOLD · CAPACITY · IN ESCROW · WAITLIST ·
 *   TO REVIEW on desktop; SOLD n/cap · ESCROW · TO REVIEW on phones).
 *
 * Create mode narrows the column to 880px and the when line reads
 * `STEP n / 5 · AUTO-SAVES AS YOU GO | SAVING… | DRAFT SAVED`.
 */
import React from 'react';
import { Countdown } from '../surface';
import { fmtWhen } from './kit';
import { money, capacityLabel } from '../../utils/surfaceFormat';

export const STATUS_PILL = {
  funding: ['FUNDING', '#FF00FF', '#fff'],
  selling: ['SELLING', '#22C55E', '#fff'],
  greenlit: ['GREENLIT', '#00FFFF', '#000'],
  processing_greenlight: ['GREENLIT', '#00FFFF', '#000'],
  draft: ['DRAFT', '#F2F2F2', '#000'],
  ended: ['ENDED', '#000', '#fff'],
  canceled: ['CANCELED', '#000', '#fff'],
  dissolved: ['DISSOLVED', '#000', '#fff'],
};

export function statusPill(status, create = false) {
  if (create) return ['NEW', '#F2F2F2', '#000'];
  return STATUS_PILL[status] || STATUS_PILL.draft;
}

export default function HostEventHeader({
  title,
  status,
  dateTime,
  create = false,
  step = 1,
  stepTotal = 5,      // create mode: 5 in Advanced, 2 in Simple
  viewWord = '',      // create mode: `SIMPLE` after the step count, nothing in Advanced
  saveState = 'idle', // create mode: idle | saving | saved
  stats = null,       // edit mode: { sold, capacity, escrowCents, waitlist, toReview }
  phone = false,
  backLabel = 'MY EVENTS',
  onBack,
  testId = 'ops-header',
}) {
  const [label, bg, fg] = statusPill(status, create);
  const ended = ['ended', 'canceled', 'dissolved'].includes(status);
  let whenLine;
  if (create) {
    const note = saveState === 'saving' ? 'SAVING…' : saveState === 'saved' ? 'DRAFT SAVED' : 'AUTO-SAVES AS YOU GO';
    whenLine = `STEP ${step} / ${stepTotal}${viewWord ? ` · ${viewWord}` : ''} · ${note}`;
  } else {
    whenLine = (
      <>
        {dateTime ? fmtWhen(dateTime) : 'NO DATE YET'}
        {' · '}
        {status === 'canceled' ? 'CANCELED' : status === 'dissolved' ? 'DISSOLVED' : ended ? 'ENDED' : status === 'draft' ? 'DRAFT' : (
          // eslint-disable-next-line react/style-prop-object -- Countdown's `style` is its format, not CSS
          <>DOORS IN <Countdown to={dateTime} style="short" expiredText="OPEN" /></>
        )}
      </>
    );
  }

  const pills = stats && (phone
    ? [
      { n: capacityLabel(stats.sold, stats.capacity), k: 'SOLD' },
      { n: money(stats.escrowCents), k: 'ESCROW', c: '#16A34A' },
      { n: String(stats.toReview), k: 'TO REVIEW' },
    ]
    : [
      { n: String(stats.sold), k: 'SOLD' },
      { n: stats.capacity ? String(stats.capacity) : '—', k: 'CAPACITY' },
      { n: money(stats.escrowCents), k: 'IN ESCROW', c: '#16A34A' },
      { n: String(stats.waitlist), k: 'WAITLIST' },
      { n: String(stats.toReview), k: 'TO REVIEW' },
    ]);

  return (
    <section className="hev-hero" data-testid={testId}>
      <div className="hev-hero-inner" data-create={create ? '' : undefined}>
        <button type="button" className="hev-back" onClick={onBack}>← {backLabel}</button>
        <div className="hev-hero-row">
          <div className="hev-hero-text">
            <span className="hev-hero-kick">
              <span className="hev-kicker">{create ? 'HOST · NEW EVENT' : 'HOST DASHBOARD'}</span>
              <span className="hev-status" style={{ background: bg, color: fg }} data-testid="ops-status">{label}</span>
            </span>
            <span className="hev-title" data-testid="ops-title">{title || 'Untitled event'}</span>
            <span className="hev-when" data-testid="ops-when">{whenLine}</span>
          </div>
          {pills && (
            <div className="hev-stats" data-testid="ops-stats">
              {pills.map((s) => (
                <span key={s.k} className="hev-stat"><b style={{ color: s.c || '#000' }}>{s.n}</b><span>{s.k}</span></span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
